package com.wintermar.inspection.core.ui.component.input

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.input.InputTransformation
import androidx.compose.foundation.text.input.TextFieldLineLimits
import androidx.compose.foundation.text.input.maxLength
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.platform.testTag
import com.wintermar.inspection.core.ui.component.input.util.InputColor
import com.wintermar.inspection.core.ui.component.input.util.InputState
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import org.jetbrains.compose.resources.painterResource

@Composable
fun InputText(
    state: InputState,
    modifier: Modifier = Modifier,
    onAction: () -> Unit = {}
) {
    val dimension = LocalAppTheme.current.dimension
    val color = LocalAppTheme.current.color
    val textStyles = LocalAppTheme.current.textStyle.body.small
    val density = LocalDensity.current
    val screenHeightDp = with(density) {
        LocalWindowInfo.current.containerSize.height.toDp()
    }
    val focusManager = LocalFocusManager.current

    OutlinedTextField(
        state = state.value,
        textStyle = textStyles,
        lineLimits = if (state.multiLine) TextFieldLineLimits.MultiLine() else TextFieldLineLimits.SingleLine,
        enabled = state.enabled,
        shape = RoundedCornerShape(dimension.radius.md),
        colors = InputColor.default(),
        isError = state.isError,
        contentPadding = PaddingValues(
            horizontal = dimension.spacing.space4,
            vertical = dimension.spacing.space1
        ),
        placeholder = { Text(state.placeholder, style = textStyles) },
        supportingText = if (state.message.isNotBlank()) {
            { Text(state.message) }
        } else null,
        inputTransformation = state.maxChar?.let {
            InputTransformation.maxLength(it)
        },
        leadingIcon = if (state.leadingIcon != null) {
            {
                Icon(
                    painter = painterResource(state.leadingIcon),
                    tint = color.icon.subtlest,
                    modifier = Modifier.size(dimension.size.icon.s),
                    contentDescription = state.label
                )
            }
        } else null,
        trailingIcon = if (state.trailingIcon != null) {
            {
                Icon(
                    painter = painterResource(state.trailingIcon!!),
                    tint = color.icon.subtlest,
                    modifier = Modifier
                        .testTag("trailing_icon")
                        .size(dimension.size.icon.s)
                        .clip(
                            shape = RoundedCornerShape(
                                size = LocalAppTheme.current.dimension.radius.full
                            )
                        )
                        .then(
                            if (state.trailingIconClickable) Modifier.clickable(
                                onClick = {
                                    onAction()
                                    focusManager.clearFocus()
                                },
                            ) else Modifier
                        ),
                    contentDescription = state.label
                )
            }
        } else null,
        onKeyboardAction = {
            focusManager.clearFocus()
        },
        modifier = modifier
            .then(
                if (state.multiLine) Modifier
                    .heightIn(
                        min = dimension.spacing.space13,
                        max = screenHeightDp * 0.9f
                    )
                else Modifier
                    .height(dimension.spacing.space8)
            )
            .onFocusChanged { focusState ->
                if (focusState.isFocused) {
                    state.hasFocus = true
                    return@onFocusChanged
                }

                if (!focusState.isFocused && state.hasFocus) {
                    state.hasFocus = false
                    state.onBlurChanged()
                }
            }
    )
}
